import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { PracticeChallenge } from "@/components/PracticeChallenge";
import { Insight } from "@/components/Insight";
import { Keyboard } from "@/components/Keyboard";
import {
  Tip, KeyIdea, Pull, Steps, Step, NextUp, KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation9() {
  const { lesson, course, accent } = findLesson("piano-foundation", "9");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>The drill has two halves: a <strong>linear walk</strong> up and down the scale (close hand), then a <strong>chord climb</strong> on the same scale (spread hand).</Highlight>
        <Highlight>In C major, ascending the full sequence is <strong>C-D-E-F-G-F-E-D-C — E-G-C</strong>. As scale degrees: <strong>1-2-3-4-5-4-3-2-1 — 3-5-8</strong>.</Highlight>
        <Highlight>For the chord part, use <strong>thumb · index · middle · pinky</strong>. Skip the ring finger — it's slightly messy here.</Highlight>
        <Highlight>The whole point is the muscle change: hand <strong>closes</strong> for the scale walk, hand <strong>spreads</strong> for the chord. Watch your fingers transition between the two shapes.</Highlight>
        <Highlight>Major to minor: flatten the third (E→E♭), both directions. The chord becomes E♭-G-C. This drill only spans degrees 1-5, so the 6th and 7th never come up.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="Two shapes, one drill" title="Close hand walks the scale · spread hand climbs the chord" accent={accent.hex}>
        <p>
          The Expansion-Compression exercise trains a movement no scale or finger drill has
          covered yet: the moment your hand has to <em>change shape</em>, mid-phrase, while
          the pulse keeps going. Specifically: <strong>five-finger compressed</strong> for the
          linear scale walk, then <strong>spread out</strong> for a chord climb on the same
          scale.
        </p>

        <KeyIdea>
          The hand closes for stepwise motion, opens for skips. Train the transition between
          those two shapes deliberately and most other piano vocabulary suddenly clicks —
          arpeggios, broken chords, scale-plus-chord patterns, all of them rest on this same
          close-spread switch.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="The pattern in C major" title="Walk up · walk down · then climb the chord" accent={accent.hex}>
        <p>The full ascending half of the drill in C major:</p>

        <p className="font-mono text-[14px] text-amber-300">
          C - D - E - F - G - F - E - D - C    (linear, close hand)<br />
          E - G - C                              (chord, spread hand)
        </p>

        <p>As scale-degree numbers, identical in any key:</p>

        <p className="font-mono text-[14px] text-emerald-300">
          1 - 2 - 3 - 4 - 5 - 4 - 3 - 2 - 1    then    3 - 5 - 8 (octave)
        </p>

        <Keyboard
          startOctave={3}
          octaves={2}
          highlights={[
            { pitch: "C4", color: "#22c55e", label: "1" },
            { pitch: "D4", color: "#22c55e", label: "2" },
            { pitch: "E4", color: "#a855f7", label: "3" },
            { pitch: "F4", color: "#22c55e", label: "4" },
            { pitch: "G4", color: "#a855f7", label: "5" },
            { pitch: "C5", color: "#a855f7", label: "8" },
          ]}
          height={170}
        />

        <PracticeChallenge
          courseSlug="piano-foundation"
          order="9"
          id="expand-compress"
          mode="sequence"
          title="Expansion–Compression (C major)"
          prompt="Walk up and back down, then climb the chord: C D E F G F E D C — E G C."
          notes={["C4", "D4", "E4", "F4", "G4", "F4", "E4", "D4", "C4", "E4", "G4", "C5"]}
          startOctave={3}
          octaves={3}
        />

        <p className="prose-nsm text-sm">
          <span className="text-emerald-300">Green</span> = the linear-scale notes (close
          hand, fingers 1-5 in five-finger position).{" "}
          <span className="text-purple-300">Purple</span> = the chord notes (spread hand,
          thumb on E, index on G, pinky on C).
        </p>

        <Tip>
          Use thumb · index · middle · pinky for the chord part — <strong>not</strong> the
          ring finger. the ring finger gets skipped here because it makes the spread shape unstable.
          Three fingers do the work cleanly; the ring takes a rest.
        </Tip>
      </Insight>

      <Insight eyebrow="The descending half" title="Mirror image — chord first, then scale down" accent={accent.hex}>
        <p>
          Once the ascending half is comfortable, reverse it. Top-down, the chord descends
          first (spread hand: <strong>C → G → E</strong> from above), then the close hand
          walks down the scale.
        </p>

        <p className="font-mono text-[14px] text-amber-300">
          C - G - E    (chord descent, spread)<br />
          C - D - E - F - G - F - E - D - C    (scale walk down, close)
        </p>

        <p>
          Same close-spread switch — just in reverse order. Watch your fingers: the spread
          shape collapses into the five-finger position the moment the chord part finishes.
        </p>
      </Insight>

      <Insight eyebrow="Both hands, with bass support" title="Sing the LH first, then add the RH" accent={accent.hex}>
        <p>
          The left hand stays simple: hold the root (Sa) of whichever scale you're in. In C
          major, that's C — pinky in the bass, on the pulse. Once it's running steadily, the
          right hand starts the close-spread drill on top.
        </p>

        <Pull>
          In fact, I would highly encourage you to sing it. Don't play the right hand
          yet. Sing it and get the singing going with the left hand.
        </Pull>

        <p>
          Singing the swaras (Sa, Re, Ga, Ma, Pa, Ma, Ga, Re, Sa — Ga, Pa, Sa) before playing
          locks the pitches in your ear. The drill turns from a finger workout into a musical
          gesture.
        </p>
      </Insight>

      <Insight eyebrow="All the natural scales" title="Move the same numbers across keys" accent={accent.hex}>
        <p>
          Once C major is comfortable, take the same number sequence (1-2-3-4-5-4-3-2-1-3-5-8)
          and move it through every white-note major scale: A, B, C, D, E, F, G. The fingering
          stays the same; only the keys under it change.
        </p>

        <Steps>
          <Step>Anchor the LH on the root of whichever scale you're in (B for B major, etc.).</Step>
          <Step>RH starts in five-finger position from that root.</Step>
          <Step>Do the linear walk first, the chord climb second. Both ascending and descending halves.</Step>
          <Step>Loop the whole thing in time with the LH pulse.</Step>
        </Steps>
      </Insight>

      <Insight eyebrow="Major → minor" title="Flatten the third — that's the whole change" accent={accent.hex}>
        <p>
          To turn the drill minor, the rule from Lesson 5 is the whole story: flatten the
          third. Because the linear walk only spans degrees 1 to 5, the sixth and seventh
          never appear — there is nothing else to change. Flatten E to E♭ going up and
          coming back, and the chord on top follows:
        </p>

        <ul className="space-y-1 text-[14px]">
          <li>The linear walk in C minor: C - D - <strong>E♭</strong> - F - G - F - E♭ - D - C</li>
          <li>The chord climb: <strong>E♭</strong> - G - C (and C - G - <strong>E♭</strong> coming down)</li>
        </ul>

        <p>
          Same fingering, one black key instead of a white one. It feels heavy and
          reflective compared to the major version — exactly the contrast this is training
          your ear to hear. (When you later stretch this drill to a full octave, natural
          minor flattens the sixth and seventh as well — in both directions, not just
          coming down.)
        </p>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-10"
        label="Lesson 10 · Fifth Chord Exercise (Our first chord!)"
        why="The hand now knows how to close and spread on cue. The next lesson uses that to build your first proper chord — a fifth — over a popular bass progression."
      />
    </LessonShell>
  );
}
